import React from 'react';

function formatStatusDetail(value) {
  return String(value || '')
    .trim()
    .replace(/^EL\s+/i, '')
    .replace(/^DEL\s+/i, '')
    .replace(/,/g, '')
    .toLowerCase();
}

function statusPresentation(note, phase) {
  const raw = String(note || '').trim();
  if (!raw) return { title: '', detail: '' };
  const lines = raw.split(/\n+/).map(line => line.trim()).filter(Boolean);
  if (lines.length > 1) {
    return { title: lines[0], detail: lines.slice(1).join(' ') };
  }
  const upper = raw.toUpperCase();
  const armadoMatch = upper.match(/^SE ARMA\s+(?:EL\s+|DEL\s+)?(.+)$/);
  if (phase === 'armado' && armadoMatch) {
    return { title: 'YA ESTA ARMADO', detail: `(${formatStatusDetail(armadoMatch[1])})` };
  }
  const desarmadoMatch = upper.match(/^SE DESARMA\s+(?:EL\s+|DEL\s+)?(.+)$/);
  if (phase === 'desarmado' && desarmadoMatch) {
    return { title: `DESARMADO EL ${formatStatusDetail(desarmadoMatch[1]).toUpperCase()}`, detail: '' };
  }
  const desarmadoStatusMatch = upper.match(/^DESARMADO\s+EL\s+(.+)$/);
  if (phase === 'desarmado' && desarmadoStatusMatch) {
    return { title: `DESARMADO EL ${formatStatusDetail(desarmadoStatusMatch[1]).toUpperCase()}`, detail: '' };
  }
  return { title: raw, detail: '' };
}

export default function AssignmentBlock({
  phase,
  section,
  sectionIndex,
  sectionItems,
  groupedAssignments,
  shouldRenderSection,
  showSectionHeader,
  isSectionActive,
  isStatusOnly,
  isSearchActive,
  isRenamingSection,
  isEditingSectionMeta,
  isEditingSectionNote,
  hasSectionMeta,
  canEdit,
  canEditSectionMeta,
  canMoveDeleteSection,
  canDeleteWholeSection,
  isFramed,
  isEmptyNamedSection,
  menuOpen,
  displaySectionTitle,
  displaySectionNote,
  displaySectionTime,
  showAssignmentSearch,
  isDropTarget,
  refKey,
  sectionDropMarker,
  defaultSection,
  assignmentModel,
  pendingRole,
  searchQuery,
  selectedKeys,
  conflictKeys,
  dragReorder,
  ctx,
}) {
  const touchGestureRef = React.useRef(null);
  if (!shouldRenderSection) return null;

  const {
    beginSectionMetaEdit,
    beginSectionNoteEdit,
    clearDropSection,
    clearSectionAssignments,
    closeMenu,
    computeMenuPos,
    deleteSection,
    displayName,
    fetchBookedPersons,
    finishPendingRole,
    focusSectionMeta,
    handlePillClick,
    handlePillPointerDown,
    handleSectionPointerDown,
    handleStatusSectionAction,
    normalizeTimeText,
    openMenu,
    openTouchAssignmentActions,
    openTouchPicker,
    openTouchRolePicker,
    options,
    patchSection,
    patchSectionMeta,
    pillKey,
    removeAssignment,
    renameSection,
    roleOptions,
    rootRef,
    searchRefs,
    sectionTimeRefs,
    sectionTitleRefs,
    selectOption,
    setActivePhase,
    setEditingSectionMeta,
    setEditingSectionNote,
    setHighlightIndex,
    setMenuPos,
    setPendingRole,
    setDropSection,
    setRenamingSection,
    setSearchPhase,
    setSearchQuery,
    setSearchSection,
    setSectionMenu,
    stopSectionChromeEvent,
    touchPicker,
  } = ctx;

  const sectionClassName = [
    'se-section',
    isFramed ? 'se-section--framed' : '',
    canMoveDeleteSection ? 'se-section--movable' : '',
    section.name !== defaultSection ? 'se-section--named' : 'se-section--general',
    hasSectionMeta ? 'se-section--has-meta' : '',
    isEmptyNamedSection ? 'se-section--empty' : '',
    isStatusOnly ? 'se-section--status' : '',
    isSectionActive ? 'se-section--active' : '',
    isDropTarget ? 'se-section--drop-target' : '',
  ].filter(Boolean).join(' ');
  const sectionLabel = displaySectionTitle || (section.name === defaultSection ? section.name : 'Etapa o lugar');
  const statusInfo = isStatusOnly ? statusPresentation(displaySectionNote, phase) : null;
  const statusActionLabel = phase === 'desarmado' ? 'desarmado posterior' : 'armado previo';

  function renderAssignmentSectionActions(extraClass = '') {
    return (
      <div className={`se-section-actions${extraClass ? ` ${extraClass}` : ''}`}>
        {section.id && section.name !== defaultSection && (
          <button
            type="button"
            className="se-section-drag-handle"
            aria-label="Mover bloque"
            title="Mover bloque"
            onPointerDown={e => handleSectionPointerDown(e, phase, section, e.currentTarget)}
            onClick={e => e.stopPropagation()}
          >::</button>
        )}
        <button
          type="button"
          className="se-section-menu-button"
          aria-label="Acciones de bloque"
          onClick={e => {
            e.stopPropagation();
            setSectionMenu(menuOpen ? null : { phase, sectionName: section.name });
          }}
        >⋯</button>
        {menuOpen && (
          <div className="se-section-menu" onClick={e => e.stopPropagation()}>
            {section.name !== defaultSection && (
              <button
                type="button"
                onClick={() => {
                  setSectionMenu(null);
                  setRenamingSection({ sectionId: section.id, phase, oldName: section.name });
                  setTimeout(() => {
                    const selector = section.id ? `[data-section-title-input="${section.id}"]` : `[data-section-title-input="${phase}--${section.name}"]`;
                    rootRef.current?.querySelector(selector)?.focus();
                  }, 0);
                }}
              >
                Editar etapa/lugar
              </button>
            )}
            {/* Agregar vive acá y no como botón flotante: en una celda con
                contenido cualquier overlay termina arriba de los nombres. La
                franja punteada queda sólo para celdas vacías, donde no hay
                nada que pisar. */}
            {ctx.createSection && (
              <button
                type="button"
                onClick={() => {
                  setSectionMenu(null);
                  ctx.createSection(phase);
                }}
              >
                Agregar etapa/lugar
              </button>
            )}
            {!ctx.singlePhase && phase === 'armado' && ctx.onCreateAuxOperation && (
              <button
                type="button"
                onClick={e => {
                  // El ancla es el bloque, que sigue vivo cuando el menú se
                  // cierra; el rect se lee sincrónicamente al abrir el popover.
                  const ancla = e.currentTarget.closest('.se-section') || e.currentTarget;
                  ctx.onCreateAuxOperation('armado', ancla);
                  setSectionMenu(null);
                }}
              >
                Armado previo (otro día)
              </button>
            )}
            {!ctx.singlePhase && phase === 'desarmado' && ctx.onCreateAuxOperation && (
              <button
                type="button"
                onClick={e => {
                  const ancla = e.currentTarget.closest('.se-section') || e.currentTarget;
                  ctx.onCreateAuxOperation('desarmado', ancla);
                  setSectionMenu(null);
                }}
              >
                Desarmado (otro día)
              </button>
            )}
            <button type="button" onClick={() => clearSectionAssignments(phase, section.name)}>
              Borrar asignaciones
            </button>
            {section.id && (
              <button type="button" onClick={() => patchSection(section.id, phase, { kind: 'assignment', note: '', time_text: '' })}>
                Vaciar hora/comentario
              </button>
            )}
            {canDeleteWholeSection && (
              <button type="button" className="se-section-menu-danger" onClick={() => deleteSection(section.id, phase, section.name)}>
                Borrar bloque
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  function handleBlockClick(e) {
    e.stopPropagation();
    if (ctx.suppressSectionClickRef.current) return;
    if (!canEdit) return;
    if (e.target.closest?.('.se-section-header, .se-section-note, .se-section-note-add, .se-section-note-input, .se-section-menu, .se-section-menu-button, .se-section-drag-handle')) {
      return;
    }
    if (isStatusOnly) {
      return;
    }
    if (touchPicker) {
      openTouchPicker(phase, section.name, e.currentTarget);
      return;
    }
    setActivePhase(phase);
    setSearchPhase(phase);
    setSearchSection(section.name);
    setTimeout(() => searchRefs.current[refKey]?.focus(), 0);
  }

  function startTouchPillGesture(e, item) {
    if (!touchPicker || !canEdit) return;
    e.stopPropagation();
    const touch = {
      x: e.clientX,
      y: e.clientY,
      opened: false,
      item,
      timer: window.setTimeout(() => {
        touch.opened = true;
        openTouchAssignmentActions({
          phase,
          section: section.name,
          name: item.person_name,
          currentRole: item.role || '',
          currentPersonId: item.person_id || null,
          assignmentId: item.id ?? null,
        });
      }, 520),
    };
    touchGestureRef.current = touch;
  }

  function moveTouchPillGesture(e) {
    const touch = touchGestureRef.current;
    if (!touch || !touchPicker) return;
    const dx = e.clientX - touch.x;
    const dy = e.clientY - touch.y;
    if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
      window.clearTimeout(touch.timer);
    }
    if (Math.abs(dx) > 54 && Math.abs(dx) > Math.abs(dy) * 1.5 && !touch.opened) {
      window.clearTimeout(touch.timer);
      touch.opened = true;
      if (dx < 0) {
        openTouchRolePicker({
          phase,
          section: section.name,
          name: touch.item.person_name,
          currentRole: touch.item.role || '',
          currentPersonId: touch.item.person_id || null,
          assignmentId: touch.item.id ?? null,
          source: 'edit',
        });
      } else {
        openTouchAssignmentActions({
          phase,
          section: section.name,
          name: touch.item.person_name,
          currentRole: touch.item.role || '',
          currentPersonId: touch.item.person_id || null,
          assignmentId: touch.item.id ?? null,
        });
      }
    }
  }

  function endTouchPillGesture(e, item) {
    const touch = touchGestureRef.current;
    if (!touch || !touchPicker) return;
    window.clearTimeout(touch.timer);
    touchGestureRef.current = null;
    if (touch.opened) return;
    const dx = Math.abs(e.clientX - touch.x);
    const dy = Math.abs(e.clientY - touch.y);
    if (dx > 12 || dy > 12) return;
    openTouchRolePicker({
      phase,
      section: section.name,
      name: item.person_name,
      currentRole: item.role || '',
      currentPersonId: item.person_id || null,
      assignmentId: item.id ?? null,
      source: 'edit',
    });
  }

  function handleSearchFocus(e) {
    setEditingSectionMeta(null);
    setSearchPhase(phase);
    setSearchSection(section.name);
    setActivePhase(phase);
    if (pendingRole?.phase === phase && pendingRole?.section === section.name) {
      setMenuPos(computeMenuPos(e.currentTarget, roleOptions(searchQuery).length));
      return;
    }
    fetchBookedPersons();
    setMenuPos(null);
  }

  function handleSearchChange(e) {
    setSearchQuery(e.target.value);
    setHighlightIndex(0);
    if (pendingRole?.phase === phase && pendingRole?.section === section.name) {
      setMenuPos(computeMenuPos(e.currentTarget, roleOptions(e.target.value).length));
      return;
    }
    openMenu(phase, e.currentTarget, e.target.value, section.name);
  }

  function handleSearchKeyDown(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeMenu();
      return;
    }
    if (pendingRole?.phase === phase && pendingRole?.section === section.name) {
      const opts = roleOptions(searchQuery);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightIndex(i => Math.min(i + 1, Math.max(0, opts.length - 1)));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightIndex(i => Math.max(0, i - 1));
        return;
      }
      if (e.key === 'Enter') {
        if (e.nativeEvent.isComposing) return;
        e.preventDefault();
        const idx = Math.min(ctx.highlightIndex, Math.max(0, opts.length - 1));
        const selectedRole = opts[idx] || e.currentTarget.value;
        finishPendingRole(selectedRole);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      if (!searchQuery.trim()) return;
      e.preventDefault();
      setHighlightIndex(i => Math.min(i + 1, Math.max(0, ctx.menuOptions.length - 1)));
      return;
    }
    if (e.key === 'ArrowUp') {
      if (!searchQuery.trim()) return;
      e.preventDefault();
      setHighlightIndex(i => Math.max(0, i - 1));
      return;
    }
    if (e.key === 'Tab') {
      closeMenu();
      if (ctx.advancePhaseTab(phase, e.shiftKey)) e.preventDefault();
      return;
    }
    if (e.key === 'Enter') {
      if (e.nativeEvent.isComposing) return;
      if (!searchQuery.trim()) return;
      e.preventDefault();
      const opts = options(phase, searchQuery, section.name);
      const idx = Math.min(ctx.highlightIndex, Math.max(0, opts.length - 1));
      if (opts[idx]) selectOption(phase, section.name, opts[idx]);
    }
  }

  return (
    <>
      {sectionDropMarker?.phase === phase && sectionDropMarker.index === sectionIndex && (
        <div className="se-section-insert-marker" />
      )}
      <div
        data-section-id={section.id || undefined}
        data-section-name={section.name}
        data-staff-drop-entity={ctx.entityKey}
        data-staff-drop-phase={phase}
        data-staff-drop-section={section.name}
        className={sectionClassName}
        onClick={handleBlockClick}
        onPointerEnter={() => setDropSection(phase, section.name)}
        onPointerLeave={() => clearDropSection(phase, section.name)}
      >
        {showSectionHeader && (
          <div
            className="se-section-header"
            onMouseDown={stopSectionChromeEvent}
            onClick={stopSectionChromeEvent}
          >
            {canEditSectionMeta && isSectionActive ? (
              <input
                autoFocus={isRenamingSection}
                className="se-section-name se-section-title-input"
                data-section-title-input={section.id || `${phase}--${section.name}`}
                ref={el => { sectionTitleRefs.current[refKey] = el; }}
                defaultValue={displaySectionTitle}
                placeholder="Etapa o lugar"
                onPointerDown={stopSectionChromeEvent}
                onMouseDown={e => {
                  e.stopPropagation();
                  beginSectionMetaEdit(phase, section);
                }}
                onFocus={e => {
                  beginSectionMetaEdit(phase, section);
                  if (isRenamingSection) e.target.select();
                }}
                onBlur={e => {
                  renameSection(section, phase, section.name, e.target.value);
                  ctx.maybeAutoDeleteEmptySection(phase, section.name, { title: e.target.value, allowDraft: true });
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    e.currentTarget.value = displaySectionTitle;
                    setRenamingSection(null);
                    setEditingSectionMeta(null);
                    e.currentTarget.blur();
                  }
                }}
                onClick={e => e.stopPropagation()}
              />
            ) : (
              <span
                className={`se-section-name${!displaySectionTitle && section.name !== defaultSection ? ' se-section-name--placeholder' : ''}`}
                onPointerDown={stopSectionChromeEvent}
                onClick={e => {
                  e.stopPropagation();
                  if (canEditSectionMeta) focusSectionMeta(phase, section, 'title');
                }}
              >{sectionLabel}</span>
            )}
            {canEditSectionMeta && isSectionActive ? (
              <input
                className="se-section-time"
                ref={el => { sectionTimeRefs.current[refKey] = el; }}
                defaultValue={displaySectionTime}
                placeholder="HH:MM"
                onPointerDown={stopSectionChromeEvent}
                onMouseDown={e => {
                  e.stopPropagation();
                  beginSectionMetaEdit(phase, section);
                }}
                onFocus={() => beginSectionMetaEdit(phase, section)}
                onBlur={e => {
                  const nextTime = normalizeTimeText(e.target.value);
                  e.target.value = nextTime;
                  patchSectionMeta(section, phase, { time_text: nextTime });
                  ctx.maybeAutoDeleteEmptySection(phase, section.name, { time_text: nextTime, allowDraft: true });
                  setEditingSectionMeta(null);
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
                  if (e.key === 'Escape') { e.preventDefault(); e.currentTarget.value = displaySectionTime; setEditingSectionMeta(null); e.currentTarget.blur(); }
                }}
                onClick={e => e.stopPropagation()}
              />
            ) : section.id || displaySectionTime ? (
              <span
                className="se-section-time se-section-time-text"
                onPointerDown={stopSectionChromeEvent}
                onClick={e => {
                  e.stopPropagation();
                  if (canEditSectionMeta) focusSectionMeta(phase, section, 'time');
                }}
              >
                {displaySectionTime || ''}
              </span>
            ) : null}
            {canMoveDeleteSection && renderAssignmentSectionActions()}
          </div>
        )}
        {!showSectionHeader && !isStatusOnly && canMoveDeleteSection && renderAssignmentSectionActions('se-section-actions--floating')}
        {isStatusOnly ? (
          <div className="se-section-status-shell">
            <div className="se-section-status-text">
              <strong>{statusInfo.title}</strong>
              {statusInfo.detail && <span>{statusInfo.detail}</span>}
            </div>
            {canMoveDeleteSection && (
              <div className="se-section-actions se-section-actions--status">
                <button
                  type="button"
                  className="se-section-menu-button"
                  aria-label={`Acciones de ${statusActionLabel}`}
                  onClick={e => {
                    e.stopPropagation();
                    setSectionMenu(menuOpen ? null : { phase, sectionName: section.name });
                  }}
                >⋯</button>
                {menuOpen && (
                  <div className="se-section-menu" onClick={e => e.stopPropagation()}>
                    <button
                      type="button"
                      onClick={e => handleStatusSectionAction?.('change-date', phase, section, e.currentTarget)}
                    >
                      Cambiar dia de {statusActionLabel}
                    </button>
                    {phase === 'armado' && (
                      <button
                        type="button"
                        onClick={e => handleStatusSectionAction?.('change-days', phase, section, e.currentTarget)}
                      >
                        Cambiar cantidad de dias
                      </button>
                    )}
                    <button
                      type="button"
                      className="se-section-menu-danger"
                      onClick={e => handleStatusSectionAction?.('delete', phase, section, e.currentTarget)}
                    >
                      Borrar {statusActionLabel}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div
            className={`se-pills${groupedAssignments.length <= 1 ? ' se-pills--single' : ''}`}
            onPointerDown={stopSectionChromeEvent}
            onMouseDown={stopSectionChromeEvent}
            onClick={stopSectionChromeEvent}
            onDoubleClick={e => {
              e.stopPropagation();
              if (canEdit) {
                setActivePhase(phase);
                setSearchPhase(phase);
                setSearchSection(section.name);
                setTimeout(() => searchRefs.current[refKey]?.focus(), 0);
              }
            }}
          >
            {groupedAssignments.map(group => (
              <div className={`se-assignment-group${group.role ? ' se-assignment-group--with-role' : ''}`} key={group.key}>
                <span className="se-assignment-people">
                  {group.items.map(item => {
                    const pillIdx = sectionItems.indexOf(item);
                    const isSelected = selectedKeys.has(pillKey(phase, item.person_name));
                    const hasConflict = conflictKeys.has(pillKey(phase, item.person_name));
                    const isUnmatched = !item.person_id;
                    const label = displayName(item);
                    let pillStyle = {};
                    if (dragReorder && dragReorder.phase === phase && (dragReorder.section || defaultSection) === section.name) {
                      if (pillIdx === dragReorder.fromIdx) {
                        pillStyle = { opacity: 0 };
                      } else {
                        let targetIdx = pillIdx;
                        if (dragReorder.toIdx > dragReorder.fromIdx && pillIdx > dragReorder.fromIdx && pillIdx <= dragReorder.toIdx) targetIdx = pillIdx - 1;
                        if (dragReorder.toIdx < dragReorder.fromIdx && pillIdx >= dragReorder.toIdx && pillIdx < dragReorder.fromIdx) targetIdx = pillIdx + 1;
                        if (targetIdx !== pillIdx && dragReorder.rects[targetIdx] && dragReorder.rects[pillIdx]) {
                          const dx = dragReorder.rects[targetIdx].left - dragReorder.rects[pillIdx].left;
                          const dy = dragReorder.rects[targetIdx].top - dragReorder.rects[pillIdx].top;
                          pillStyle = { transform: `translate(${dx}px,${dy}px)`, transition: 'transform 0.15s ease' };
                        }
                      }
                    }
                    return (
                      <span
                        key={item.id ?? `${item.person_name}-${pillIdx}`}
                        className={`se-pill se-pill--assignment${group.role ? ' se-pill--with-role' : ''}${isSelected ? ' se-pill--selected' : ''}${hasConflict ? ' se-pill--conflict' : ''}${isUnmatched ? ' se-pill--unmatched' : ''}`}
                        style={pillStyle}
                        title={hasConflict ? `${label} - conflicto de horario` : (canEdit ? (touchPicker ? 'Tocar para rol · izquierda rol · derecha acciones' : 'Arrastrar · Click para rol') : label)}
                        onPointerDown={canEdit ? e => {
                          if (touchPicker) {
                            startTouchPillGesture(e, item);
                            return;
                          }
                          handlePillPointerDown(e, phase, section.name, item.person_name, item.role, e.currentTarget);
                        } : undefined}
                        onPointerMove={touchPicker && canEdit ? moveTouchPillGesture : undefined}
                        onPointerUp={touchPicker && canEdit ? e => endTouchPillGesture(e, item) : undefined}
                        onPointerCancel={touchPicker && canEdit ? () => {
                          if (touchGestureRef.current?.timer) window.clearTimeout(touchGestureRef.current.timer);
                          touchGestureRef.current = null;
                        } : undefined}
                        onClick={e => {
                          e.stopPropagation();
                          if (touchPicker) return;
                          if (canEdit) handlePillClick(e, phase, item.person_name, item.id);
                        }}
                      >
                        <span className="se-pill-text">
                          <b>{label}</b>
                        </span>
                        {canEdit && !touchPicker && (
                          <button
                            type="button"
                            className="se-remove"
                            aria-label={`Quitar ${label}`}
                            onPointerDown={e => e.stopPropagation()}
                            onMouseDown={e => e.stopPropagation()}
                            onClick={e => { e.stopPropagation(); removeAssignment(phase, item.person_name, section.name); }}
                          >×</button>
                        )}
                      </span>
                    );
                  })}
                </span>
                {group.role && <span className="se-pill-role-suffix">{group.role}</span>}
              </div>
            ))}
          </div>
        )}
        {canEdit && !isStatusOnly && (
          <div className={`se-combo${showAssignmentSearch ? ' se-combo--active' : ''}${touchPicker ? ' se-combo--touch' : ''}`}>
            {touchPicker ? (
              <button
                type="button"
                className="se-search se-search--touch"
                onPointerDown={stopSectionChromeEvent}
                onMouseDown={stopSectionChromeEvent}
                onClick={e => {
                  e.stopPropagation();
                  openTouchPicker(
                    phase,
                    section.name
                  );
                }}
              >
                {pendingRole?.phase === phase && pendingRole?.section === section.name
                  ? `Elegir rol para ${pendingRole.name}`
                  : '+ persona'}
              </button>
            ) : (
              <input
                ref={el => { searchRefs.current[refKey] = el; }}
                className="se-search"
                type="text"
                autoComplete="off"
                tabIndex={showAssignmentSearch ? undefined : -1}
                placeholder={pendingRole?.phase === phase && pendingRole?.section === section.name
                  ? `Rol para ${pendingRole.name}...`
                  : `Buscar ${assignmentModel.labels.person.toLowerCase()}...`}
                value={isSearchActive ? searchQuery : ''}
                onPointerDown={stopSectionChromeEvent}
                onMouseDown={stopSectionChromeEvent}
                onFocus={handleSearchFocus}
                onChange={handleSearchChange}
                onKeyDown={handleSearchKeyDown}
                onClick={e => e.stopPropagation()}
              />
            )}
          </div>
        )}
        {canEdit && !isStatusOnly && (section.id || isSearchActive || isEditingSectionMeta || displaySectionNote) && (
          isEditingSectionNote || displaySectionNote ? (
            isEditingSectionNote ? (
              <textarea
                autoFocus
                className="se-section-note-input"
                defaultValue={displaySectionNote}
                placeholder="Comentario al pie..."
                onPointerDown={stopSectionChromeEvent}
                onMouseDown={stopSectionChromeEvent}
                onFocus={e => {
                  e.stopPropagation();
                  setSearchPhase(null);
                  setSearchQuery('');
                  setMenuPos(null);
                  setPendingRole(null);
                }}
                onBlur={e => {
                  patchSectionMeta(section, phase, { kind: 'assignment', note: e.target.value });
                  ctx.maybeAutoDeleteEmptySection(phase, section.name, { note: e.target.value, allowDraft: true });
                  setEditingSectionNote(null);
                }}
                onKeyDown={e => {
                  if (e.key === 'Escape') { e.preventDefault(); setEditingSectionNote(null); }
                  if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
                }}
                onClick={e => e.stopPropagation()}
              />
            ) : (
              <button
                type="button"
                className="se-section-note"
                onPointerDown={stopSectionChromeEvent}
                onMouseDown={stopSectionChromeEvent}
                onClick={e => { e.stopPropagation(); beginSectionNoteEdit(section, phase); }}
              >{displaySectionNote}</button>
            )
          ) : isSectionActive ? (
            <button
              type="button"
              className="se-section-note-add"
              onPointerDown={stopSectionChromeEvent}
              onMouseDown={e => {
                e.stopPropagation();
              }}
              onClick={e => {
                e.stopPropagation();
                beginSectionNoteEdit(section, phase);
              }}
            >+ comentario</button>
          ) : null
        )}
      </div>
    </>
  );
}
